import React, { useState } from 'react';
import { Button, Collapse, Modal, Table as BootstrapTable } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { faExchangeAlt, faRandom } from '@fortawesome/free-solid-svg-icons';
import dateFormat from 'dateformat';
import { CustomButton } from '../../components/CustomButton';
import { NoDataComponent } from '../../components/NoDataComponent';
import { PaginationComponent } from '../../components/Pagination';
import { ReturnedItem, ReturnedItemsGroup, ReturnedItemsTableInnerProps } from '../../models/table-models';
import { checkered } from '../../styles/selectStyles';
import { date } from '../../utils/Enums/Common';
import { inventoryQuantitiesTableHeaders, TableHeaders } from '../../utils/Enums/TableHeaders';
import { AddEditInventory } from '../stock/AddEditInventory';

type ReturnedItemsTableRow = ReturnedItem & {
    returnedItemsDate: string;
    selectionKey: string;
};

type ReturnedItemsDateGroup = {
    dateKey: string;
    dateLabel: string;
    items: ReturnedItemsTableRow[];
};

type ReturnedItemsUserGroup = {
    userKey: string;
    userName: string;
    itemsCount: number;
    items: ReturnedItemsTableRow[];
    dates: ReturnedItemsDateGroup[];
};

const selectedReturnedItemRowStyle = {
    '--bs-table-bg': '#cfe2ff',
    '--bs-table-accent-bg': '#cfe2ff',
    backgroundColor: '#cfe2ff',
    userSelect: 'none',
} as React.CSSProperties;

const returnedItemRowStyle = {
    userSelect: 'none',
} as React.CSSProperties;

const ReturnedItemsBulkAction = ({
    items,
    selectedItems = [],
    refetchEntitiesHook,
    icon = faExchangeAlt,
}: {
    items: ReturnedItem[];
    selectedItems?: ReturnedItem[];
    refetchEntitiesHook: () => void;
    icon?: typeof faExchangeAlt;
}) => {
    const [showModal, setShowModal] = useState(false);
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [actionItems, setActionItems] = useState<ReturnedItem[]>(items);
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });

    const openRedistributionModal = (itemsToRedistribute: ReturnedItem[]) => {
        setActionItems(itemsToRedistribute);
        setShowChoiceModal(false);
        setShowModal(true);
    };

    return (
        <>
            <CustomButton
                icon={icon}
                iconClass='fa-fw'
                onClick={(event) => {
                    event.stopPropagation();
                    setShowChoiceModal(true);
                }}
                className='me-1 color-main'
            />
            <Modal show={showChoiceModal} onHide={() => setShowChoiceModal(false)} backdrop='static' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='returned-items-action-choice'>{t('confirm')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItems.length > 0
                        ? t('confirmMoveSelectedReturnedItems')
                        : t('confirmMoveAllReturnedItems')}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowChoiceModal(false)}>{t('noAction')}</Button>
                    <Button
                        className='btn-success'
                        onClick={() => openRedistributionModal(selectedItems.length > 0 ? selectedItems : items)}
                    >
                        {t('ok')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop='static' size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='contained-modal-title-vcenter'>{tableT('edit')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AddEditInventory
                        itemToEdit={actionItems}
                        setShowModal={setShowModal}
                        refetchEntitiesHook={refetchEntitiesHook}
                    />
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                    <Button form='form' type='submit' className='btn-success'>
                        {t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export const ReturnedItemsInner = ({
    getEntities,
    pagination,
    refetchEntitiesHook,
    groupByDate = true,
}: ReturnedItemsTableInnerProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const { items, pager } = getEntities();
    const [expandedUser, setExpandedUser] = useState<string>();
    const [expandedDates, setExpandedDates] = useState<string[]>([]);
    const [selectedReturnedItemKeys, setSelectedReturnedItemKeys] = useState<string[]>([]);
    const [lastSelectedReturnedItemKey, setLastSelectedReturnedItemKey] = useState<string>();

    const getReturnedAt = (item: ReturnedItem, group: ReturnedItemsGroup) => item.returnedAt ?? group.returnedAt;

    const getReturnedItemsDateKey = (returnedAt?: string) => {
        return returnedAt ? dateFormat(returnedAt, 'yyyy-mm-dd') : 'missing-returned-at';
    };

    const formatReturnedItemsDate = (returnedAt?: string) => {
        return returnedAt ? dateFormat(returnedAt, date) : '-';
    };

    const returnedItemGroups = Object.values(
        items.reduce<
            Record<string, Omit<ReturnedItemsUserGroup, 'dates'> & { dates: Record<string, ReturnedItemsDateGroup> }>
        >((groups, group) => {
            const userKey = `${group.userId}`;

            if (!groups[userKey]) {
                groups[userKey] = {
                    userKey,
                    userName: group.userName,
                    itemsCount: 0,
                    items: [],
                    dates: {},
                };
            }

            groups[userKey].itemsCount += group.itemsCount ?? group.items.length;
            group.items.forEach((item, itemIndex) => {
                const returnedAt = getReturnedAt(item, group);
                const dateKey = getReturnedItemsDateKey(returnedAt);
                const dateLabel = formatReturnedItemsDate(returnedAt);
                const returnedItem = {
                    ...item,
                    returnedItemsDate: dateLabel,
                    selectionKey:
                        item.id != null
                            ? `${item.id}`
                            : `${group.userId}-${item.contentId}-${item.passportId}-${item.batchCode}-${returnedAt}-${itemIndex}`,
                };

                groups[userKey].items.push(returnedItem);

                if (!groups[userKey].dates[dateKey]) {
                    groups[userKey].dates[dateKey] = {
                        dateKey,
                        dateLabel,
                        items: [],
                    };
                }

                groups[userKey].dates[dateKey].items.push(returnedItem);
            });

            return groups;
        }, {})
    ).map((group) => ({
        ...group,
        dates: Object.values(group.dates),
    }));

    const toggleUser = (userKey: string) => {
        setExpandedUser((current) => (current === userKey ? undefined : userKey));
        setExpandedDates([]);
        setSelectedReturnedItemKeys([]);
        setLastSelectedReturnedItemKey(undefined);
    };

    const toggleDate = (dateKey: string) => {
        setExpandedDates((current) =>
            current.includes(dateKey) ? current.filter((key) => key !== dateKey) : [...current, dateKey]
        );
    };

    const isReturnedItemSelected = (item: ReturnedItemsTableRow) => {
        return selectedReturnedItemKeys.includes(item.selectionKey);
    };

    const toggleReturnedItemSelection = (
        item: ReturnedItemsTableRow,
        dateItems: ReturnedItemsTableRow[],
        event: React.MouseEvent<HTMLTableRowElement>
    ) => {
        if (event.shiftKey && lastSelectedReturnedItemKey != null) {
            const startIndex = dateItems.findIndex(({ selectionKey }) => selectionKey === lastSelectedReturnedItemKey);
            const endIndex = dateItems.findIndex(({ selectionKey }) => selectionKey === item.selectionKey);

            if (startIndex >= 0 && endIndex >= 0) {
                const fromIndex = Math.min(startIndex, endIndex);
                const toIndex = Math.max(startIndex, endIndex);
                const rangeKeys = dateItems
                    .slice(fromIndex, toIndex + 1)
                    .map(({ selectionKey }) => selectionKey);

                setSelectedReturnedItemKeys((current) => Array.from(new Set([...current, ...rangeKeys])));
                setLastSelectedReturnedItemKey(item.selectionKey);
                return;
            }
        }

        setSelectedReturnedItemKeys((current) =>
            current.includes(item.selectionKey)
                ? current.filter((key) => key !== item.selectionKey)
                : [...current, item.selectionKey]
        );
        setLastSelectedReturnedItemKey(item.selectionKey);
    };

    const refetchReturnedItemsAndClearSelection = () => {
        setSelectedReturnedItemKeys([]);
        setLastSelectedReturnedItemKey(undefined);
        refetchEntitiesHook();
    };

    const returnedItemTableHeaders = groupByDate
        ? [
              ...inventoryQuantitiesTableHeaders.slice(0, 2),
              TableHeaders.availableAmount,
              ...inventoryQuantitiesTableHeaders.slice(2),
          ]
        : [
              TableHeaders.returnedAt,
              ...inventoryQuantitiesTableHeaders.slice(0, 2),
              TableHeaders.availableAmount,
              ...inventoryQuantitiesTableHeaders.slice(2),
          ];

    const renderReturnedItemsTable = (returnedItems: ReturnedItemsTableRow[]) => (
        <table className='w-100'>
            <thead className='background-dark color-main'>
                <tr>
                    {returnedItemTableHeaders.map((column, index) => (
                        <th key={index} className='single-line-text table-header text-center fw-bold p-2'>
                            {tableT(column)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {returnedItems.map((item, itemIndex) => {
                    const isSelected = isReturnedItemSelected(item);

                    return (
                        <tr
                            key={item.id ?? itemIndex}
                            className={`${isSelected ? '' : checkered(itemIndex)} cursor-pointer`}
                            style={isSelected ? selectedReturnedItemRowStyle : returnedItemRowStyle}
                            onClick={(event) => toggleReturnedItemSelection(item, returnedItems, event)}
                        >
                            {!groupByDate && <td className='p-3'>{item.returnedItemsDate}</td>}
                            <td className='p-3'>{item.passportName}</td>
                            <td className='p-3'>{item.count}</td>
                            <td className='p-3'>{item.availableAmount ?? '-'}</td>
                            <td className='p-3'>{item.state != null ? tableT(item.state) : item.state}</td>
                            <td className='p-3'>{item.userName}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <h3 className='align-items-start w-100 mb-3'>{t('returnedItems')}</h3>
                    <div className='w-100 rounded'>
                        <BootstrapTable responsive>
                            <thead className='background-dark color-main'>
                                <tr>
                                    <th className='single-line-text table-header text-center fw-bold'>
                                        {tableT(TableHeaders.user)}
                                    </th>
                                    <th className='single-line-text table-header text-center fw-bold'>
                                        {tableT(TableHeaders.count)}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className='accordionTableRow cursor-pointer'>
                                {returnedItemGroups.map((group, groupIndex) => (
                                    <React.Fragment key={group.userKey}>
                                        <tr
                                            className={`hovered-row ${checkered(groupIndex)}`}
                                            onClick={() => toggleUser(group.userKey)}
                                        >
                                            <td>{group.userName}</td>
                                            <td>{group.itemsCount}</td>
                                        </tr>
                                        <Collapse in={expandedUser === group.userKey}>
                                            <tr>
                                                <td colSpan={2} className='p-2'>
                                                    {groupByDate ? (
                                                        <table className='w-100'>
                                                            <thead className='background-dark color-main'>
                                                                <tr>
                                                                    <th className='single-line-text table-header text-center fw-bold p-2'>
                                                                        {tableT(TableHeaders.returnedAt)}
                                                                    </th>
                                                                    <th className='single-line-text table-header text-center fw-bold p-2'>
                                                                        {tableT('actions')}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {group.dates.map((dateGroup, dateIndex) => {
                                                                    const dateRowKey = `${group.userKey}-${dateGroup.dateKey}`;
                                                                    const selectedDateItems =
                                                                        dateGroup.items.filter(isReturnedItemSelected);

                                                                    return (
                                                                        <React.Fragment key={dateRowKey}>
                                                                            <tr
                                                                                className={`hovered-row ${checkered(
                                                                                    dateIndex
                                                                                )}`}
                                                                                onClick={() => toggleDate(dateRowKey)}
                                                                            >
                                                                                <td className='p-3'>
                                                                                    {dateGroup.dateLabel}
                                                                                </td>
                                                                                <td className='d-flex justify-content-center border-none'>
                                                                                    <ReturnedItemsBulkAction
                                                                                        items={dateGroup.items}
                                                                                        selectedItems={
                                                                                            selectedDateItems
                                                                                        }
                                                                                        refetchEntitiesHook={
                                                                                            refetchReturnedItemsAndClearSelection
                                                                                        }
                                                                                        icon={
                                                                                            selectedDateItems.length > 0
                                                                                                ? faRandom
                                                                                                : faExchangeAlt
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                            <Collapse
                                                                                in={expandedDates.includes(dateRowKey)}
                                                                            >
                                                                                <tr>
                                                                                    <td colSpan={2} className='p-2'>
                                                                                        {renderReturnedItemsTable(
                                                                                            dateGroup.items
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            </Collapse>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <>
                                                            <div className='d-flex justify-content-end mb-2'>
                                                                <ReturnedItemsBulkAction
                                                                    items={group.items}
                                                                    selectedItems={group.items.filter(
                                                                        isReturnedItemSelected
                                                                    )}
                                                                    refetchEntitiesHook={
                                                                        refetchReturnedItemsAndClearSelection
                                                                    }
                                                                    icon={
                                                                        group.items.some(isReturnedItemSelected)
                                                                            ? faRandom
                                                                            : faExchangeAlt
                                                                    }
                                                                />
                                                            </div>
                                                            {renderReturnedItemsTable(group.items)}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        </Collapse>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </BootstrapTable>
                    </div>
                    <PaginationComponent
                        recordsCount={pager.totalCount}
                        pagination={pagination}
                    />
                </>
            ) : (
                <NoDataComponent noDataText={t('returnedItems')} />
            )}
        </>
    );
};
